import { useMemo, useCallback } from "react";
import qs from "qs";
import { MUIGrid, MUISelectChangeEvent, SelectV2 } from "@cobee/cobee-ui";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { CategorySelect, StateSelect } from "../../pages/RequestsPage";

export default function RequestFilters({
  statesConfig,
  companyId,
  selectedStates,
  refetch,
  handleChangeFilter,
  categoriesConfig,
  selectedCategories,
}: {
  statesConfig: StateSelect[];
  companyId: string;
  selectedStates: string[];
  refetch: any;
  handleChangeFilter: any;
  categoriesConfig: CategorySelect[];
  selectedCategories: string[];
}) {
  const history = useHistory();

  const { t } = useTranslation();

  const stateParams = useMemo(
    () =>
      statesConfig
        .filter(({ text }) => selectedStates.indexOf(text) !== -1)
        .map(({ id }) => id),
    [statesConfig, selectedStates]
  );
  const categoriesParams = useMemo(
    () =>
      categoriesConfig
        .filter(({ text }) => selectedCategories.indexOf(text) !== -1)
        .map(({ id }) => id),
    [selectedCategories, categoriesConfig]
  );

  const getUrlParams = useCallback(
    ({ type, newParams }) => ({
      state: qs.stringify({ state: stateParams }, { arrayFormat: "brackets" }),
      category: qs.stringify(
        { category: categoriesParams },
        { arrayFormat: "brackets" }
      ),
      [type]: newParams,
    }),
    [categoriesParams, stateParams]
  );

  const urlParamsConfig = ({ state, category }) => ({
    state: category !== "" ? `${state}&${category}` : state,
    category: state !== "" ? `${state}&${category}` : category,
  });

  const newParamsConfig = useMemo(
    () => ({
      state: { state: stateParams },
      category: { category: categoriesParams },
    }),
    [stateParams, categoriesParams]
  );

  const refetchRequest = useCallback(
    ({ filterType }) => {
      const replacedParams = getUrlParams({
        type: filterType,
        newParams: qs.stringify(newParamsConfig[filterType], {
          arrayFormat: "brackets",
        }),
      });
      const { state, category } = replacedParams;
      const urlParams = urlParamsConfig({ state, category })[filterType];

      const parsedUrlParams = urlParams !== "" ? `&${urlParams}` : "";

      history.replace(
        `/v2/employee-requests?companyId=${companyId}${parsedUrlParams}`
      );
      refetch();
    },
    [companyId, getUrlParams, history, newParamsConfig, refetch]
  );

  return (
    <MUIGrid container gap="16px">
      <SelectV2
        multiple
        value={selectedStates}
        label={t("v2.common.states")}
        width="150px"
        onChange={(event: MUISelectChangeEvent<StateSelect>) => {
          const {
            target: { value },
          } = event;
          handleChangeFilter({ type: "state", value });
        }}
        onClose={() => {
          refetchRequest({ filterType: "state" });
        }}
        items={statesConfig.map(({ id, text: textValue }) => ({
          id,
          label: textValue,
        }))}
      />

      <SelectV2
        multiple
        value={selectedCategories}
        label={t("v2.common.benefits")}
        width="150px"
        onChange={(event: MUISelectChangeEvent<CategorySelect>) => {
          const {
            target: { value },
          } = event;
          handleChangeFilter({ type: "category", value });
        }}
        onClose={() => {
          refetchRequest({ filterType: "category" });
        }}
        items={categoriesConfig.map(({ id, text: textValue }) => ({
          id,
          label: textValue,
        }))}
      />
    </MUIGrid>
  );
}
